import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaPaperPlane } from 'react-icons/fa';
import { useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const industries = [
  "HVAC",
  "Plumbing",
  "Electrical",
  "Cleaning",
  "Garage Door",
  "Auto Body",
  "Snow Removal",
  "Other"
];

const FormSection = styled.section`
  padding: 8rem 2rem;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
  font-family: 'Urbanist', sans-serif;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => props.theme.isDark ? `
      radial-gradient(circle at 0% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 100% 100%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)
    ` : `
      radial-gradient(circle at 0% 0%, rgba(79, 70, 229, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 100% 100%, rgba(124, 58, 237, 0.03) 0%, transparent 50%)
    `};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContentSide = styled.div``;

const Title = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 1.5rem;
  
  span {
    background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 2rem;
  line-height: 1.8;
`;

const FormSide = styled(motion.div)`
  border-radius: 24px;
  border: 1px solid ${props => props.theme.colors.border};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: auto;
  min-height: 664px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.isDark ? 
      '0 8px 30px rgba(0, 0, 0, 0.4)' : 
      '0 8px 30px rgba(79, 70, 229, 0.12)'
    };
  }

  @media (max-width: 968px) {
    padding: 0;
    min-height: auto;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background: ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.03)' : 'white'};
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  background: ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'white'};
  color: ${props => props.theme.colors.text.primary};
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
  }
`;

const StyledPhoneInput = styled.div`
  .PhoneInput {
    padding: 0.5rem;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 12px;
    background: ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'white'};
    
    input {
      background: transparent;
      border: none;
      color: ${props => props.theme.colors.text.primary};
      font-size: 1rem;
      padding: 0.5rem;
      width: 100%;
      
      &:focus {
        outline: none;
      }
    }
  }

  .PhoneInput--focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  padding: 1rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  color: #10b981;
  text-align: center;
  padding: 1rem;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 12px;
`;

const ErrorMessage = styled(motion.div)`
  color: #ef4444;
  text-align: center;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 12px;
`;

const Select = styled.select`
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  background: ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'white'};
  color: ${props => props.theme.colors.text.primary};
  font-size: 1rem;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
  }
`;

const NameFieldsContainer = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const Form = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: ''
  });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setError('');

    try {
      const response = await fetch('https://services.leadconnectorhq.com/hooks/6RrfDNzFZ39pjc2rFImT/webhook-trigger/5e175df3-7b83-405f-8b7f-2aaf053e7a15', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      setStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: ''
      });
    } catch (err) {
      setStatus('error');
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <FormSection>
      <Container>
        <Grid>
          <ContentSide>
            <Title
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Join Our <span>Waitlist</span>
            </Title>
            <Description
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Be among the first to experience ServTrackr. Sign up now for early access and exclusive benefits.
            </Description>
          </ContentSide>

          <FormSide
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <StyledForm onSubmit={handleSubmit}>
              <NameFieldsContainer>
                <InputGroup>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    required
                  />
                </InputGroup>
              </NameFieldsContainer>

              <InputGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </InputGroup>

              <InputGroup>
                <Label>Phone Number</Label>
                <StyledPhoneInput>
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={formData.phone}
                    onChange={(value) => setFormData({...formData, phone: value})}
                  />
                </StyledPhoneInput>
              </InputGroup>

              <InputGroup>
                <Label>Company</Label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                />
              </InputGroup>

              <InputGroup>
                <Label>Service Industry</Label>
                <Select
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  required
                >
                  <option value="">Select your industry</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </Select>
              </InputGroup>

              {status === 'success' && (
                <SuccessMessage
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  Thank you for joining our waitlist! We'll be in touch soon.
                </SuccessMessage>
              )}

              {status === 'error' && (
                <ErrorMessage
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </ErrorMessage>
              )}

              <SubmitButton 
                type="submit" 
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting...' : 'Join Waitlist'}
                <FaPaperPlane />
              </SubmitButton>
            </StyledForm>
          </FormSide>
        </Grid>
      </Container>
    </FormSection>
  );
};

export default Form;